import * as React from 'react'
import TextField from '@mui/material/TextField'
import Dialog from '@mui/material/Dialog'
import DialogContent from '@mui/material/DialogContent'
import DialogTitle from '@mui/material/DialogTitle'
import {
    Alert,
    AlertTitle,
    Autocomplete,
    Box,
    Button,
    Chip,
    Divider,
    Grid,
    IconButton,
    Stack,
    Typography,
    useMediaQuery,
} from '@mui/material'
import LockRoundedIcon from '@mui/icons-material/LockRounded'
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded'
import CloseRoundedIcon from '@mui/icons-material/CloseRounded'
import { ClientsContext, clientErrorMessage } from '../context/clientsContext'
import { CasesContext } from '../context/casesContext'
import { SessionsContext } from '../context/sessionsContext'
import { DialogsContext } from '../context/dialogsContext'
import { Controller, useForm } from 'react-hook-form'
import {
    SubmissionFeedback, CaseType, User, caseTypeOptions, CaseTypeType,
    CaseCategoryType, FakeUser,
} from '../types'
import { LoadingButton } from '@mui/lab'
import ConfirmDialog from './ConfirmDialog'
import { theme } from './SessionsTab/AreaStateSetterStyle'
import { getDisplayCaseState } from './ClientsTab/ClientsTable'
import { displayEditEnabledCaseStates } from '../constants'
import { buildPreSubmitContent, needsPreSubmitModal } from '../utils/caseSubmission'
import PreSubmitCaseModal from './PreSubmitCaseModal'


// -----------------------------------------------------------------------
// Submission feedback alert
// -----------------------------------------------------------------------
// Two outcomes share this component: the submit endpoint refused the case
// (blocking reasons), or it accepted it with guidance attached (advisory
// reasons). See heal.services.progress.evaluate_case_against_rules.

interface SubmissionFeedbackAlertProps {
    result: SubmissionFeedback;
    onDismiss: () => void;
}

const SubmissionFeedbackAlert: React.FC<SubmissionFeedbackAlertProps> = ({ result, onDismiss }) => {
    // Completing the course arrives as a blocking reason like any other, but it
    // is an achievement, and it outranks anything else in the list.
    const isCourseComplete = result.blocking_reasons.some((r) => r.code === 'COURSE_COMPLETE');
    return (
    <Alert
        severity={result.failed ? 'error' : isCourseComplete ? 'success' : result.blocked ? 'warning' : 'info'}
        sx={{ mb: 2, alignItems: 'flex-start' }}
        action={
            <IconButton
                aria-label='Dismiss'
                size='small'
                color='inherit'
                onClick={onDismiss}
            >
                <CloseRoundedIcon fontSize='small' />
            </IconButton>
        }
    >
        <AlertTitle sx={{ mb: 0.5 }}>
            {result.failed
                ? 'Nothing was changed'
                : isCourseComplete ? 'Course complete — nothing left to submit'
                : result.blocked ? 'Can’t submit this case' : 'Submitted — one thing to note'}
        </AlertTitle>
        <Stack spacing={0.75} sx={{ mt: 0.5 }}>
            {result.failed && result.blocking_reasons.length === 0 && (
                <Typography variant='body2'>
                    The case is still where it was. Please try again — if it keeps
                    failing, check your connection or sign in again.
                </Typography>
            )}
            {result.blocking_reasons.map((reason, idx) => (
                <Box
                    key={`${reason.code}-${reason.rule_id ?? idx}`}
                    sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.75 }}
                >
                    {isCourseComplete
                        ? <CheckCircleRoundedIcon sx={{ fontSize: 16, mt: '2px', color: 'success.main' }} />
                        : <LockRoundedIcon sx={{ fontSize: 16, mt: '2px', color: 'warning.dark' }} />}
                    <Typography variant='body2' sx={{ flex: 1 }}>{reason.message}</Typography>
                </Box>
            ))}
            {result.advisory_reasons.length > 0 && (
                <Box sx={{ mt: result.blocked ? 0.5 : 0 }}>
                    {result.blocked && (
                        <Typography variant='caption' sx={{ display: 'block', color: 'text.secondary', mb: 0.25 }}>
                            Heads up
                        </Typography>
                    )}
                    {result.advisory_reasons.map((reason, idx) => (
                        <Typography
                            key={`adv-${reason.code}-${reason.rule_id ?? idx}`}
                            // On the accepted path this is the whole message, so it
                            // gets full weight rather than the secondary treatment.
                            variant={result.blocked ? 'caption' : 'body2'}
                            sx={{
                                display: 'block',
                                color: result.blocked ? 'text.secondary' : 'inherit',
                            }}
                        >
                            {result.blocked ? `• ${reason.message}` : reason.message}
                        </Typography>
                    ))}
                </Box>
            )}
        </Stack>
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 1 }}>
            <Button size='small' color='inherit' onClick={onDismiss}>
                {result.failed ? 'Close'
                    : isCourseComplete ? 'Great'
                    : result.blocked ? 'OK, understood' : 'Done'}
            </Button>
        </Box>
    </Alert>
    );
};


export default function ManageCaseDialog() {
    const { showManageCaseDialog, manageCaseDialogState } = React.useContext(DialogsContext);
    const { cases, updateCase, deleteCase, caseCategories, toggleCaseStatus, refreshCases } = React.useContext(CasesContext);
    const { clients, scopeKey, resendWaiver } = React.useContext(ClientsContext);
    const [deleteError, setDeleteError] = React.useState<string | null>(null);
    const deleteSessionKey = `${scopeKey}:${manageCaseDialogState.caseId}:${manageCaseDialogState.show}`;
    const deleteSessionRef = React.useRef({ key: deleteSessionKey });
    if (deleteSessionRef.current.key !== deleteSessionKey) deleteSessionRef.current = { key: deleteSessionKey };
    const mounted = React.useRef(true);
    const [loading, setLoading] = React.useState(false);
    const [loadingSubmit, setLoadingSubmit] = React.useState(false);
    const [showConfirmDialog, setShowConfirmDialog] = React.useState(false);
    const [caseToEdit, setCaseToEdit] = React.useState<CaseType | null>(null);
    const [client, setClient] = React.useState<User | null>(null);
    const [clientWaiverAccepted, setClientWaiverAccepted] = React.useState<boolean | null>(null);
    const [submitButtonText, setSubmitButtonText] = React.useState('Submit for Review');
    const [showWaiverIssueDialog, setShowWaiverIssueDialog] = React.useState(false);
    const [caseStatus, setCaseStatus] = React.useState<string | null>(null);
    const { sessions } = React.useContext(SessionsContext);
    const [showPreSubmitModal, setShowPreSubmitModal] = React.useState(false);
    const [preSubmitTitle, setPreSubmitTitle] = React.useState('');
    const [preSubmitMessage, setPreSubmitMessage] = React.useState<React.ReactNode>('');
    // Set when the backend refuses a submit, or accepts one with guidance
    // attached. Rendered inline; the dialog stays open so the user can read it.
    const [feedback, setFeedback] = React.useState<SubmissionFeedback | null>(null);
    const [missingTypeError, setMissingTypeError] = React.useState(false);
    const isSmDown = useMediaQuery(theme.breakpoints.down('sm'));
    const { control, setValue, getValues, handleSubmit } = useForm<AddCaseType>({
        defaultValues:
        {
            name: '',
            client: FakeUser,
            type: { id: 0, label: '' },
            categories: [] as CaseCategoryType[],
        }

    });

    type AddCaseType = {
        name: string;
        client: User | undefined;
        type: CaseTypeType | undefined;
        categories: CaseCategoryType[];
    }

    React.useEffect(() => {
        if (manageCaseDialogState) {
            const tempCase = cases.find((c) => c.id === manageCaseDialogState.caseId);
            if (tempCase) {
                setCaseToEdit(tempCase);
                setCaseStatus(getDisplayCaseState(tempCase));
                const tempClient = clients.find((c) => c.id === tempCase?.client);
                if (tempClient) {
                    setClient(tempClient);
                }
            }
        }
    }, [manageCaseDialogState, cases, clients])

    const handleClose = () => {
        deleteSessionRef.current = { key: deleteSessionKey };
        setDeleteError(null);
        setShowConfirmDialog(false);
        showManageCaseDialog({ show: false });
        setValue('name', '');
        setValue('type', undefined);
        setValue('client', undefined);
        setValue('categories', []);
        setLoading(false);
        setFeedback(null);
        setMissingTypeError(false);
    }

    React.useEffect(() => {
        mounted.current = true;
        return () => { mounted.current = false; };
    }, []);

    React.useEffect(() => {
        setDeleteError(null);
        setLoading(false);
        setShowConfirmDialog(false);
    }, [deleteSessionKey]);

    const updateCaseFields = async () => {
        const { client: formClient, name, type, categories } = getValues();
        // `type` defaults to a placeholder object, so it is always truthy —
        // the label is what tells us whether a type was actually chosen.
        const caseType = type?.label?.trim().toLowerCase();
        if (!caseType) {
            setMissingTypeError(true);
            return false;
        }
        setMissingTypeError(false);
        if (caseToEdit && formClient != null && categories) {
            const newCase = {
                caseId: caseToEdit.id,
                caseName: name,
                caseType,
                caseCategories: categories.filter((c) => c.id !== 0)
            };
            try {
                await updateCase(newCase);
                return true;
            } catch (e: any) {
                console.error('updateCaseFields error: ' + e);
                const serverMessage = e?.response?.data?.error;
                setFeedback({
                    blocked: true,
                    failed: true,
                    case_id: caseToEdit.id,
                    blocking_reasons: serverMessage
                        ? [{ code: 'SAVE_FAILED', rule_id: null, message: serverMessage }]
                        : [],
                    advisory_reasons: [],
                });
                return false;
            }
        }
        return false;
    };

    const handleUpdate = async () => {
        setLoading(true);
        const ok = await updateCaseFields();
        if (ok) {
            refreshCases();
            handleClose();
        }
        setLoading(false);
    }


    const handleConfirmPreSubmit = async () => {
        setShowPreSubmitModal(false);
        if (!(clientWaiverAccepted || !client?.tracked)) {
            setShowWaiverIssueDialog(true);
            return;
        }
        if (!caseToEdit) return;
        setLoadingSubmit(true);
        const ok = await updateCaseFields();
        if (ok) {
            const result = await toggleCaseStatus(caseToEdit.id, true);
            await refreshCases();
            if (result) {
                // Either refused, or accepted with guidance — both need reading,
                // so keep the dialog open.
                setFeedback(result);
                setLoadingSubmit(false);
                return;
            }
            handleClose();
        }
        setLoadingSubmit(false);
    };

    const submit = async () => {
        if (!caseToEdit) return;
        const isCurrentlySubmitted = caseStatus === 'submitted';
        const isSubmitting = !isCurrentlySubmitted;
        // Any new submission attempt clears a stale message from a previous
        // try so the alert doesn't confuse the user.
        setFeedback(null);
        if (!(clientWaiverAccepted || !client?.tracked)) {
            setShowWaiverIssueDialog(true);
            return;
        }
        // Unsubmit path
        if (!isSubmitting) {
            // No field save here: the backend refuses content changes while a case
            // is with a reviewer, so this could only ever resend identical values.
            setLoadingSubmit(true);
            const result = await toggleCaseStatus(caseToEdit.id, false);
            await refreshCases();
            if (result) {
                setFeedback(result);
                setLoadingSubmit(false);
                return;
            }
            handleClose();
            setLoadingSubmit(false);
            return;
        }
        // Use unsaved form type if changed
        const { type: formType } = getValues();
        const effectiveCaseType = formType?.label ? formType.label.toLowerCase() : caseToEdit.type;
        const courseId = caseToEdit.cohort?.course?.id;
        const sessionCount = sessions ? sessions.filter((s) => s.case === caseToEdit.id).length : 0;
        if (needsPreSubmitModal(effectiveCaseType, courseId, sessionCount)) {
            const { title, message } = buildPreSubmitContent(effectiveCaseType, courseId!);
            setPreSubmitTitle(title);
            setPreSubmitMessage(message);
            setShowPreSubmitModal(true);
            return;
        }
        setLoadingSubmit(true);
        const ok = await updateCaseFields();
        if (ok) {
            const result = await toggleCaseStatus(caseToEdit.id, true);
            await refreshCases();
            if (result) {
                setFeedback(result);
                setLoadingSubmit(false);
                return;
            }
            handleClose();
        }
        setLoadingSubmit(false);
    }

    const handleDelete = async () => {
        if (!caseToEdit || loading || !cases.some(item => item.id === caseToEdit.id)) return;
        const session = deleteSessionRef.current;
        const isCurrent = () => mounted.current && deleteSessionRef.current === session;
        setDeleteError(null);
        setLoading(true);
        try {
            await deleteCase({ caseId: caseToEdit.id });
            if (isCurrent()) handleClose();
        } catch (error) {
            if (isCurrent()) {
                setDeleteError(clientErrorMessage(error, 'Unable to confirm the case was deleted. Refresh your cases before retrying.'));
            }
        } finally {
            if (isCurrent()) setLoading(false);
        }
    }

    React.useEffect(() => {
        if (manageCaseDialogState && manageCaseDialogState.caseId && caseToEdit && client) {
            const tempType = caseTypeOptions.find((t) => t.label.toLowerCase() === caseToEdit?.type);
            setValue('name', caseToEdit?.name || '');
            setValue('type', tempType);
            setValue('client', client);
            setValue('categories', caseToEdit?.categories == undefined ? [] as CaseCategoryType[] : caseToEdit?.categories);
        }
    }, [manageCaseDialogState, caseToEdit, client]);

    React.useEffect(() => {
        if (caseToEdit) {
            setSubmitButtonText(caseStatus === 'submitted' ? 'Unsubmit' : 'Submit for Review');
        }
    }, [cases, caseToEdit]);

    React.useEffect(() => {
        if (caseToEdit && client && client.tracked) {
            const waiverResult = client?.waivers_results.find((wr) => wr.case_id === caseToEdit.id && wr.waiver_id === 2);
            setClientWaiverAccepted(waiverResult?.accepted === 'Y');
        }
    }, [cases, caseToEdit, client]);

    const getIsSubmitCaseDisabled = () => {
        if (caseStatus && displayEditEnabledCaseStates.includes(caseStatus)) {
            return false;
        }
        return true;
    }

    const isUpdateDisabled = () => {
        if (caseStatus) {
            return !displayEditEnabledCaseStates.includes(caseStatus);
        }
        return true;
    }

    // Every queue state (in review, escalated) is shown to the healer as
    // "submitted". Changing type or categories there would move the case under
    // the reviewer reading it, so the backend refuses those edits.
    const isWithReviewer = caseStatus === 'submitted';

    return (
        <div>
            <Dialog open={!!manageCaseDialogState.show} onClose={handleClose} keepMounted={false}>
                <DialogTitle>{'Manage Case'}</DialogTitle>
                <Divider />
                <DialogContent sx={{ minWidth: isSmDown ? 300 : 500 }}>
                    {deleteError && <Alert severity='error' sx={{ mb: 2 }}>{deleteError}</Alert>}
                    <form onSubmit={handleSubmit(submit)}>
                        <Controller
                            name="name"
                            control={control}
                            render={({ field }) => (
                                <TextField
                                    fullWidth
                                    label="Case Name (avoid using client name)"
                                    variant="outlined"
                                    {...field}
                                    required
                                />
                            )}
                        />
                        <Box m={1} />
                        <Controller
                            control={control}
                            name='client'
                            rules={{ required: true }}
                            render={({ field: { onChange, value } }) => (
                                <Autocomplete
                                    disabled={true}
                                    noOptionsText={'No Clients'}
                                    onChange={(event, client) => {
                                        onChange(client)
                                    }}
                                    value={value}
                                    options={clients}
                                    getOptionLabel={(client) => (client ? client.full_name || client.email : '')}
                                    isOptionEqualToValue={(option, value) => option.id === value.id}
                                    renderInput={(params) => (
                                        <TextField
                                            {...params}
                                            label='Client'
                                            margin='normal'
                                            variant='outlined'
                                            required
                                        />
                                    )}
                                />
                            )}
                        />
                        <Controller
                            control={control}
                            name='type'
                            rules={{ required: true }}
                            render={({ field: { onChange, value } }) => (
                                <Autocomplete
                                    disabled={isWithReviewer}
                                    noOptionsText={'No Types'}
                                    onChange={(event, type) => {
                                        onChange(type);
                                        if (type?.label) setMissingTypeError(false);
                                    }}
                                    value={value}
                                    options={caseTypeOptions}
                                    getOptionLabel={(type) => (type ? type.label : '')}
                                    isOptionEqualToValue={(option, value) => option.id === value.id}
                                    renderInput={(params) => (
                                        <TextField
                                            {...params}
                                            label='Case Type'
                                            margin='normal'
                                            variant='outlined'
                                            required
                                            error={missingTypeError}
                                            helperText={
                                                missingTypeError
                                                    ? 'Choose a case type before saving or submitting.'
                                                    : isWithReviewer
                                                        ? 'Unsubmit to change the type or categories.'
                                                        : undefined
                                            }
                                        />
                                    )}
                                />
                            )}
                        />
                        <Controller
                            control={control}
                            name='categories'
                            render={({ field: { onChange, value } }) => (
                                <Autocomplete
                                    multiple
                                    disabled={isWithReviewer}
                                    noOptionsText={'No Categories Selected'}
                                    onChange={(event, type) => onChange(type)}
                                    value={value}
                                    options={[...caseCategories || [], ...[{ id: 0, name: 'None of the above' }]]}
                                    getOptionLabel={(c) => c.name || ''}
                                    isOptionEqualToValue={(option, value) => option.id === value.id}
                                    renderInput={(params) => (
                                        <TextField
                                            {...params}
                                            label='Case Categories'
                                            margin='normal'
                                            variant='outlined'
                                            InputProps={{
                                                ...params.InputProps,
                                                required: caseStatus !== 'submitted' && value.length === 0
                                            }}
                                        />
                                    )}
                                />
                            )}
                        />
                        <Box m={2} />
                        {/* Sits beside the buttons so the answer lands where the
                            healer just tapped, without scripted scrolling. */}
                        {feedback && (
                            <SubmissionFeedbackAlert
                                result={feedback}
                                onDismiss={() => {
                                    setFeedback(null);
                                    // Nothing left to act on once guidance is read.
                                    if (!feedback.blocked) handleClose();
                                }}
                            />
                        )}
                        <Grid item>
                            <LoadingButton disabled={isUpdateDisabled()} loading={loading && !loadingSubmit} variant='contained' onClick={() => caseStatus != 'submitted' ? handleUpdate() : alert('cant edit Submitted case')} color='primary' fullWidth>
                                Update
                            </LoadingButton>
                            <Box m={1} />
                            <LoadingButton disabled={getIsSubmitCaseDisabled()} loading={loadingSubmit} variant='contained' type={'submit'} color='secondary' fullWidth>
                                {submitButtonText}
                            </LoadingButton>
                            <Box m={3} />
                            <Button variant='text' disabled={loading || isUpdateDisabled()} onClick={() => setShowConfirmDialog(true)} color='error' fullWidth>
                                Delete
                            </Button>
                        </Grid>
                    </form>
                </DialogContent>
            </Dialog>
            <ConfirmDialog
                open={showWaiverIssueDialog}
                title="Submission Error"
                message="Client has not accepted healing waiver. The client can accept the waiver by using the 'client login' at pranarecords.app or you can send them a link to their email by selecting 'Resend Waiver'."
                onConfirm={() => {
                    if (caseToEdit) {
                        resendWaiver(caseToEdit.id);
                    }
                    setShowWaiverIssueDialog(false);
                }}
                onCancel={() => {
                    setShowWaiverIssueDialog(false);
                }}
                confirmText='Resend Waiver'
                cancelText='Ok'
            />
            <ConfirmDialog
                open={showConfirmDialog}
                title="Warning"
                message="Deleting this Case will also erase all Sessions linked to it, you can not undo this. Do you want to continue?"
                onConfirm={() => {
                    setShowConfirmDialog(false);
                    handleDelete();
                }}
                onCancel={() => {
                    setShowConfirmDialog(false);
                }}
            />
            <PreSubmitCaseModal
                open={showPreSubmitModal}
                title={preSubmitTitle}
                message={preSubmitMessage}
                loading={loadingSubmit}
                onConfirm={handleConfirmPreSubmit}
                onCancel={() => setShowPreSubmitModal(false)}
            />
        </div>
    )
}
